<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="$t('button.manage_hr_profile')"
  >
    <v-card-text
      class="pa-6"
    >
      <v-btn-toggle
        v-if="hasGpsModule"
        v-model="currentProfile"
        color="primary"
        mandatory
        class="v-btn-toggle--block mb-5"
      >
        <v-btn
          v-if="hasGpsModule"
          value="speed"
          @click="currentProfile = 'speed'"
        >
          {{ $t('player_performance.profile.speed.label') }}
        </v-btn>
        <v-btn
          v-if="hasGpsModule"
          value="accelerations"
          @click="currentProfile = 'accelerations'"
        >
          {{ $t('player_performance.profile.accelerations.label') }}
        </v-btn>
        <v-btn
          v-if="hasHeartRateModule"
          value="HR"
          @click="currentProfile = 'HR'"
        >
          {{ $t('player_performance.profile.heart_rate.label') }}
        </v-btn>
      </v-btn-toggle>

      <div v-if="currentProfile === 'HR'">
        <validation-observer ref="validation">
          <div class="mb-3">
            <subtitle>
              {{ $t('player_performance.profile.heart_rate.title') }}
            </subtitle>
          </div>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: hrProfile.method === 'HRMAX' }"
              >
                <v-text-field
                  v-model="hrProfile.HRMAX"
                  :error-messages="errors"
                  :label="$t('hr_variables.max_heart_rate')"
                  :placeholder="$t('label.e_g') + ' ' + 180"
                  outlined
                  type="number"
                  @input="calculate()"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.HRREST"
                  :error-messages="errors"
                  :label="$t('hr_variables.resting_heart_rate')"
                  :placeholder="$t('label.e_g') + ' ' + 60"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: hrProfile.method === 'HRANP' }"
              >
                <v-text-field
                  v-model="hrProfile.HRANPT"
                  :error-messages="errors"
                  :label="$t('text_input.anaerobic_threshold.title')"
                  :placeholder="$t('label.e_g') + ' ' + 180"
                  outlined
                  type="number"
                  @input="calculate()"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <div class="mb-3">
            <subtitle>
              {{ $t('player_performance.profile.calculate_heart_rate_zones') }}
            </subtitle>
          </div>

          <v-btn-toggle
            v-model="hrProfile.method"
            color="primary"
            mandatory
            density="compact"
            class="v-btn-toggle--block mb-5"
            @change="calculate()"
          >
            <v-btn
              value="HRMAX"
              small
            >
              {{ $t('calculate_heart_rate_zones.per_max_hr') }}
            </v-btn>
            <v-btn
              value="HRANP"
              small
            >
              {{ $t('calculate_heart_rate_zones.per_anaerobic_threshold') }}
            </v-btn>
          </v-btn-toggle>
        </validation-observer>

        <div v-if="hrProfile.method === 'HRMAX'">
          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREG.lower"
                  :error-messages="errors"
                  :label="$t('text_input.first_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREG.upper"
                  :error-messages="errors"
                  :label="$t('text_input.first_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRRE.lower"
                  :error-messages="errors"
                  :label="$t('text_input.second_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRRE.upper"
                  :error-messages="errors"
                  :label="$t('text_input.second_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRAI.lower"
                  :error-messages="errors"
                  :label="$t('text_input.third_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRAI.upper"
                  :error-messages="errors"
                  :label="$t('text_input.third_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRANP.lower"
                  :error-messages="errors"
                  :label="$t('text_input.fourth_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRANP.upper"
                  :error-messages="errors"
                  :label="$t('text_input.fourth_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRVO2MAX.lower"
                  :error-messages="errors"
                  :label="$t('text_input.fifth_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRVO2MAX.upper"
                  :error-messages="errors"
                  :label="$t('text_input.fifth_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </div>
        <div v-else>
          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREGANPM.lower"
                  :error-messages="errors"
                  :label="$t('text_input.first_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREGANPM.upper"
                  :error-messages="errors"
                  :label="$t('text_input.first_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREANPM.lower"
                  :error-messages="errors"
                  :label="$t('text_input.second_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRREANPM.upper"
                  :error-messages="errors"
                  :label="$t('text_input.second_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRAIANPM.lower"
                  :error-messages="errors"
                  :label="$t('text_input.third_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRAIANPM.upper"
                  :error-messages="errors"
                  :label="$t('text_input.third_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRANPANPM.lower"
                  :error-messages="errors"
                  :label="$t('text_input.fourth_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRANPANPM.upper"
                  :error-messages="errors"
                  :label="$t('text_input.fourth_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRVO2MAXANPM.lower"
                  :error-messages="errors"
                  :label="$t('text_input.fifth_threshold_min.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="hrProfile.calculations.HRVO2MAXANPM.upper"
                  :error-messages="errors"
                  :label="$t('text_input.fifth_threshold_max.title')"
                  outlined
                  type="number"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </div>
      </div>

      <div v-if="currentProfile === 'accelerations'">
        <validation-observer ref="validation">
          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_1.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone1_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_1.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone1_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_2.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone2_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_2.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone2_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_3.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone3_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_3.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone3_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_4.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone4_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="accelerationProfile.ZONE_4.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.accelerations.zone4_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('m/s²')"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </validation-observer>
      </div>

      <div v-if="currentProfile === 'speed'">
        <validation-observer ref="validation">
          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_1.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone1_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_1.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone1_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_2.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone2_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_2.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone2_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_3.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone3_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_3.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone3_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_4.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone4_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_4.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone4_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_5.lower"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone5_min')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="speedProfile.ZONE_5.upper"
                  :error-messages="errors"
                  :label="$t('manage_performance_profile.speed.zone5_max')"
                  outlined
                  type="number"
                  :suffix="getUnitLabel('km/h')"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </validation-observer>
      </div>
      <v-row v-if="type === 'team'">
        <v-col>
          {{ $t("overwrite_player_profiles") }}
        </v-col>
        <v-col cols="auto">
          <v-switch
            v-model="overwriteProfiles"
            hide-details
            class="ma-0 pa-0"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="saving"
            @click="save()"
          >
            {{ $t("button.save") }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>
  </card-dialog>
</template>
<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import Subtitle from '@/components/helpers/Subtitle.vue'
import { cloneDeep } from 'lodash'
import { convertAccelerationToImperial, convertSpeed, getUnitSuffixForUser } from '@/utils/helpers'

const calculationThresholds = {
  HRREG: {
    lower: 0,
    upper: 0.6
  },
  HRRE: {
    lower: 0.6,
    upper: 0.75
  }, // 75
  HRAI: {
    lower: 0.75,
    upper: 0.85
  }, // 85
  HRANP: {
    lower: 0.85,
    upper: 0.9
  }, // 95
  HRVO2MAX: {
    lower: 0.90,
    upper: 1
  },
  HRREGANPM: {
    lower: 0,
    upper: 0.65
  },
  HRREANPM: {
    lower: 0.65,
    upper: 0.8
  },
  HRAIANPM: {
    lower: 0.8,
    upper: 0.9
  },
  HRANPANPM: {
    lower: 0.9,
    upper: 1
  },
  HRVO2MAXANPM: {
    lower: 1,
    upper: 2
  }
}

export default {
  name: 'PerformanceProfileDialog',
  components: {
    CardDialog,
    Subtitle
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      hrProfile: {
        HRMAX: '',
        HRREST: '',
        HRANPT: '',
        method: 'HRMAX',
        calculations: {
          HRREG: {
            lower: '',
            upper: ''
          },
          HRRE: {
            lower: '',
            upper: ''
          },
          HRAI: {
            lower: '',
            upper: ''
          },
          HRANP: {
            lower: '',
            upper: ''
          },
          HRVO2MAX: {
            lower: '',
            upper: ''
          },
          HRREGANPM: {
            lower: '',
            upper: ''
          },
          HRREANPM: {
            lower: '',
            upper: ''
          },
          HRAIANPM: {
            lower: '',
            upper: ''
          },
          HRANPANPM: {
            lower: '',
            upper: ''
          },
          HRVO2MAXANPM: {
            lower: '',
            upper: ''
          }
        }
      },
      saving: false,
      currentProfile: 'speed',
      speedProfile: {},
      accelerationProfile: {},
      overwriteProfiles: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    unitSystem() {
      return this.user?.organisation_settings?.unit_system || 'metric'
    },
    hasGpsModule() {
      return this.user?.organisation_settings?.has_gps_module || false
    },
    hasHeartRateModule() {
      return this.user?.organisation_settings?.has_heart_rate_module || false
    }
  },
  watch: {
    modalVisible(value) {
      if (!value) return

      if (!this.hasGpsModule) this.currentProfile = 'HR'

      if (this.data.hr_profile) this.hrProfile = cloneDeep(this.data.hr_profile)
      if (this.data.speed_profile) this.speedProfile = cloneDeep(this.data.speed_profile)
      if (this.data.acceleration_profile) this.accelerationProfile = cloneDeep(this.data.acceleration_profile)
    }
  },
  methods: {
    calculate() {
      let runCalculation = true

      if (this.hrProfile.method === 'HRMAX' && !this.hrProfile.HRMAX) runCalculation = false
      if (this.hrProfile.method === 'HRANP' && !this.hrProfile.HRANPT) runCalculation = false

      if (!runCalculation) {
        for (const variable of Object.keys(calculationThresholds)) {
          this.hrProfile.calculations[variable].lower = ''
          this.hrProfile.calculations[variable].upper = ''
        }

        return
      }

      for (const variable of Object.keys(calculationThresholds)) {
        this.hrProfile.calculations[variable].lower = Math.round(calculationThresholds[variable].lower * this.hrProfile[this.hrProfile.method === 'HRMAX' ? 'HRMAX' : 'HRANPT'])
        this.hrProfile.calculations[variable].upper = Math.round(calculationThresholds[variable].upper * this.hrProfile[this.hrProfile.method === 'HRMAX' ? 'HRMAX' : 'HRANPT'])
      }
    },
    save() {
      const speedProfileMetersPerMinute = {
        ZONE_1: {},
        ZONE_2: {},
        ZONE_3: {},
        ZONE_4: {},
        ZONE_5: {}
      }

      for (const zone of Object.keys(this.speedProfile)) {
        speedProfileMetersPerMinute[zone].lower = convertSpeed(this.speedProfile[zone].lower, getUnitSuffixForUser('km/h'))
        speedProfileMetersPerMinute[zone].upper = convertSpeed(this.speedProfile[zone].upper, getUnitSuffixForUser('km/h'))
      }

      const accelerationProfile = {
        ZONE_1: {},
        ZONE_2: {},
        ZONE_3: {},
        ZONE_4: {}
      }

      for (const zone of Object.keys(this.accelerationProfile)) {
        accelerationProfile[zone].lower = convertAccelerationToImperial(this.accelerationProfile[zone].lower, true)
        accelerationProfile[zone].upper = convertAccelerationToImperial(this.accelerationProfile[zone].upper, true)
      }

      this.data.speed_profile = this.speedProfile
      this.data.acceleration_profile = this.accelerationProfile

      this.$emit('save', {
        hr_profile: this.hrProfile,
        speed_profile: speedProfileMetersPerMinute,
        acceleration_profile: accelerationProfile,
        overwrite_profiles: this.overwriteProfiles
      })

      this.modalVisible = false
    },
    getUnitLabel(metricUnit) {
      if (this.unitSystem === 'metric') return this.$t(`suffixes.${metricUnit}`)

      const map = {
        'km/h': 'mph',
        'm/s²': 'ft/s²'
      }

      const imperialUnit = map[metricUnit]

      if (!imperialUnit) return this.$t(`suffixes.${metricUnit}`)

      return this.$t(`suffixes.${imperialUnit}`)
    }
  }
}
</script>
